<script setup lang="ts">
import { Head, router, useForm } from "@inertiajs/vue3"
import { useDebounceFn } from "@vueuse/core"
import { DownloadIcon, SearchIcon } from "lucide-vue-next"
import { watch } from "vue"

import Heading from "@/components/Heading.vue"
import Badge from "@/components/custom/Badge.vue"
import InputDebounced from "@/components/custom/InputDebounced.vue"
import Pagination from "@/components/custom/Pagination.vue"
import Table from "@/components/custom/Table.vue"
import StudyApproveDialog from "@/components/manager/StudyApproveDialog.vue"
import { Button } from "@/components/ui/button"
import { InputGroup, InputGroupAddon } from "@/components/ui/input-group"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AppLayout from "@/layouts/AppLayout.vue"
import { dateFormatter } from "@/lib/formatters"
import { download } from "@/lib/utils"
import { formatPrice } from "@/lib/utils"
import { exportManagerStudiesPath } from "@/routes"
import { managerStudiesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { ManagerStudiesIndex, ManagerStudy } from "@/types/serializers"
import type { Column } from "@/types/table"

const props = defineProps<ManagerStudiesIndex>()

const formFilters = useForm<{
  search?: string
  status?: string
  sort_by?: string
  order?: string
}>({
  search: props.query.search || undefined,
  status: props.query.status || undefined,
  sort_by: props.query.sort_by || undefined,
  order: props.query.order || undefined,
})

const columns: Array<Column<ManagerStudy>> = [
  {
    key: "id",
    header: "ID",
    sortable: true,
  },
  {
    key: "user_ref",
    header: "Comercia ID",
    sortable: true,
  },
  {
    key: "user_full_name",
    header: "Empleado",
    sortable: true,
  },
  {
    key: "training_type",
    header: "Tipo de Formación",
    sortable: true,
  },
  {
    key: "provider",
    header: "Proveedor",
    sortable: true,
  },
  {
    key: "course_name",
    header: "Curso",
    sortable: true,
  },
  {
    key: "status",
    header: "Estado",
  },
  {
    key: "total_amount",
    header: "Importe",
    right: true,
    sortable: true,
  },
  {
    key: "created_at",
    header: "Fecha de solicitud",
    right: true,
    sortable: true,
  },
  {
    key: "updated_at",
    header: "Fecha de resolucion",
    right: true,
    sortable: true,
  },
  {
    id: "action",
    key: "id",
    right: true,
    header: "Acción",
    thClass: "w-fit",
    tdClass: "w-fit",
  },
]

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Ayuda de Estudios",
    href: managerStudiesPath(),
  },
]

const statusItems = [
  { value: null, label: "Todos los estados" },
  { value: "pending", label: "EN REVISIÓN" },
  { value: "approved", label: "APROBADO" },
  { value: "rejected", label: "RECHAZADO" },
]

async function exportStudies() {
  const XCSRFToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute("content")
  if (!XCSRFToken) {
    console.error("CSRF token not found")
    return
  }
  try {
    const response = await fetch(exportManagerStudiesPath(), {
      method: "POST",
      headers: { "X-CSRF-Token": XCSRFToken },
    })

    if (!response.ok) throw new Error("Export failed")

    const blob = await response.blob()
    console.log(response.headers.get("Content-Disposition"))
    const filename =
      response.headers
        .get("Content-Disposition")
        ?.match(/filename="(.+)"/)?.[1] || `studies-${Date.now()}.xlsx`
    download(blob, filename)
  } catch (error) {
    console.error("Export error:", error)
  }
}

function refreshData() {
  router.get(
    managerStudiesPath(),
    { ...props.query, ...formFilters.data(), page: undefined },
    { preserveState: true, preserveScroll: true },
  )
}

const debouncedRefreshData = useDebounceFn(refreshData, 100)

watch(formFilters, debouncedRefreshData)
</script>

<template>
  <Head :title="breadcrumbs[breadcrumbs.length - 1].title" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="px-6 py-4">
      <Heading
        title="Ayuda de Estudios"
        description="Gestiona reembolsos de equipamiento"
      />

      <div class="mt-4 flex items-center gap-2">
        <InputGroup class="w-88">
          <InputDebounced
            :debounce="400"
            group
            placeholder="Buscar por nombre, equipo o departamento..."
            v-model="formFilters.search"
          />
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
        </InputGroup>

        <Select v-model="formFilters.status">
          <SelectTrigger class="w-44">
            <SelectValue placeholder="Todos los estados" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="item in statusItems"
              :key="item.label"
              :value="item.value"
            >
              {{ item.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          class="ml-auto"
          :disabled="!records.length"
          @click="exportStudies"
        >
          <DownloadIcon />
          Export .xlsx
        </Button>
      </div>
    </div>

    <Table
      :data="records"
      :columns="columns"
      v-model:sort-by="formFilters.sort_by"
      v-model:order="formFilters.order"
    >
      <template #user_ref-cell="{ row }">
        {{ row.user.ref }}
      </template>

      <template #user_full_name-cell="{ row }">
        {{ row.user.full_name }}
      </template>

      <template #total_amount-cell="{ row }">
        {{ formatPrice(row.total_amount) }}
      </template>

      <template #status-cell="{ row }">
        <Badge class="w-fit" :title="row.reject_reason" :status="row.status" />
      </template>

      <template #created_at-cell="{ row }">
        {{ dateFormatter(row.created_at, {}, "dateTime") }}
      </template>

      <template #updated_at-cell="{ row }">
        {{ dateFormatter(row.updated_at, {}, "dateTime") }}
      </template>

      <template #action-cell="{ row }">
        <StudyApproveDialog :data="row" />
      </template>

      <template #empty>No hay solicitudes</template>
    </Table>

    <Pagination class="my-4 mt-auto px-6" />
  </AppLayout>
</template>
